import { Type, type Static } from '@sinclair/typebox';
import { SeoQaIssueKind } from '../translation_namespace/seo_content_namespace';

/**
 * Sortie du rédacteur et rapport de QA (plan §4.3 jobs 3 et 4). Le moteur
 * `seo_content/engine` produit un `SeoArticleOutput` + un `SeoQaReport` ; les
 * orchestrations (Payload, `pro_news`) ne voient rien d'autre.
 */

export const SeoArticleFaqItemSchema = Type.Object({
  question: Type.String(),
  answer: Type.String(),
});
export type SeoArticleFaqItem = Static<typeof SeoArticleFaqItemSchema>;

export const SeoArticleImageSchema = Type.Object({
  /** `HERO` ou `INLINE`. */
  role: Type.Union([Type.Literal('HERO'), Type.Literal('INLINE')]),
  /** URL servable (S3 présignée courte ou URL publique). */
  url: Type.String(),
  alt: Type.String(),
  width: Type.Optional(Type.Integer()),
  height: Type.Optional(Type.Integer()),
  bytes: Type.Optional(Type.Integer()),
  /** Provenance (« pexels »), identifiant et licence, pour la traçabilité. */
  source: Type.Optional(Type.String()),
  source_id: Type.Optional(Type.String()),
  source_url: Type.Optional(Type.String()),
  credit: Type.Optional(Type.String()),
  /** Clé S3 si l'image a été copiée chez nous. */
  storage_key: Type.Optional(Type.String()),
  /** Index du bloc HTML après lequel insérer une image `INLINE` (absent = hero). */
  after_block: Type.Optional(Type.Integer({ minimum: 0 })),
});
export type SeoArticleImage = Static<typeof SeoArticleImageSchema>;

/** Trace d'un appel LLM (provider, modèle, coût) : une entrée par étape. */
export const SeoModelTraceSchema = Type.Object({
  step: Type.String(),
  provider: Type.String(),
  model: Type.String(),
  ms: Type.Integer({ minimum: 0 }),
  prompt_tokens: Type.Optional(Type.Integer({ minimum: 0 })),
  completion_tokens: Type.Optional(Type.Integer({ minimum: 0 })),
  failed_over: Type.Optional(Type.Boolean()),
  /** Reprise : part des phrases du jet précédent retrouvées telles quelles (contrôle de dérive). */
  unchanged_ratio: Type.Optional(Type.Number({ minimum: 0, maximum: 1 })),
  /** Précision lisible (ex. : pourquoi une passe d'édition a été jetée). */
  note: Type.Optional(Type.String()),
});
export type SeoModelTrace = Static<typeof SeoModelTraceSchema>;

export const SeoArticleOutputSchema = Type.Object({
  title: Type.String(),
  slug: Type.String(),
  markdown: Type.String(),
  /** HTML propre (parseur markdown réel), sans H1, prêt pour la cible. */
  html: Type.String(),
  meta_title: Type.String(),
  meta_description: Type.String(),
  excerpt: Type.String(),
  /** FAQ extraite de la section « Questions fréquentes » (JSON-LD FAQPage). */
  faq: Type.Array(SeoArticleFaqItemSchema),
  images: Type.Array(SeoArticleImageSchema),
  word_count: Type.Integer({ minimum: 0 }),
  model_trace: Type.Array(SeoModelTraceSchema),
});
export type SeoArticleOutput = Static<typeof SeoArticleOutputSchema>;

export const SeoQaIssueSchema = Type.Object({
  kind: Type.Enum(SeoQaIssueKind),
  /** Origine : `score` (règle déterministe) ou `judge` (juge LLM, citation vérifiée). */
  origin: Type.Union([Type.Literal('score'), Type.Literal('judge')]),
  /** Clé de règle (score) ou identifiant de détecteur (juge). */
  rule: Type.String(),
  message: Type.String(),
  /** Extrait exact de l'article concerné, quand il existe. */
  quote: Type.Optional(Type.String()),
  /** Intertitre de la section concernée, quand il est connu. */
  section: Type.Optional(Type.String()),
  blocking: Type.Boolean(),
});
export type SeoQaIssue = Static<typeof SeoQaIssueSchema>;

/** Verdict du juge LLM v2 : uniquement des constats vérifiables + trois notes indicatives. */
export const SeoQaJudgeSchema = Type.Object({
  provider: Type.String(),
  model: Type.String(),
  ms: Type.Integer({ minimum: 0 }),
  /** Constats retenus (citation retrouvée dans le texte). */
  verified_claims: Type.Integer({ minimum: 0 }),
  /** Constats écartés (citation introuvable) : mesure la fiabilité du juge. */
  dropped_claims: Type.Integer({ minimum: 0 }),
  /** Notes indicatives /10, jamais utilisées pour la gate. */
  utility: Type.Optional(Type.Number({ minimum: 0, maximum: 10 })),
  specificity: Type.Optional(Type.Number({ minimum: 0, maximum: 10 })),
  naturalness: Type.Optional(Type.Number({ minimum: 0, maximum: 10 })),
  summary: Type.Optional(Type.String()),
  errored: Type.Optional(Type.String()),
});
export type SeoQaJudge = Static<typeof SeoQaJudgeSchema>;

export const SeoQaReportSchema = Type.Object({
  score_version: Type.String(),
  score: Type.Integer({ minimum: 0, maximum: 100 }),
  grade: Type.String(),
  /** Clés des règles bloquantes en échec (mode strict). */
  blocking_rules: Type.Array(Type.String()),
  failed_rules: Type.Array(Type.String()),
  warned_rules: Type.Array(Type.String()),
  judge: Type.Optional(SeoQaJudgeSchema),
  issues: Type.Array(SeoQaIssueSchema),
  /** Nombre de passes de rédaction (1 = premier jet, 2-3 = reprises). */
  iterations: Type.Integer({ minimum: 1 }),
  passed: Type.Boolean(),
});
export type SeoQaReport = Static<typeof SeoQaReportSchema>;
